class ProductDetailSticky extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.scrollThreshold = this.getAttribute("scroll-threshold") || 300;
    // optional own threshold from 750px wide (pre-BF 2026: the sign-up box pushes the buy button
    // down on desktop, so the bar comes in earlier there; phones keep scroll-threshold)
    this.desktopThreshold = this.getAttribute("scroll-threshold-desktop");
    this.desktopQuery = window.matchMedia("(min-width: 750px)");
    this.fadeInDuration = 300;
  }

  connectedCallback() {
    this.setupScrollListener();
    this.setupInitialState();
    this.setupPriceSync();
    this.setupRevealAfterChoice();
  }

  disconnectedCallback() {
    this.removeScrollListener();
    this.teardownPriceSync();
    this.teardownRevealAfterChoice();
  }

  /* ------------------------------------------------------------------------
     REVEAL AFTER CHOICE (reveal="after-choice", phones only)
     The bar stays hidden until the customer has made a choice — a variant
     change, or closing the colour sheet (kc-fabric-sheet:close, which also
     covers keeping the colour that was already selected). The flag lives on
     window because a variant change re-renders the section and replaces this
     element.
     After that it follows the steps: it shows once the colour step ("Farbe
     wählen", the step accordion of the colour picker) has reached the upper
     half of the screen, and hides again while the customer is back up at the
     model step or the gallery. Without that step on the page, it simply
     stays once a choice was made.
     ------------------------------------------------------------------------ */
  setupRevealAfterChoice() {
    this.revealAfterChoice = this.getAttribute("reveal") === "after-choice";
    if (!this.revealAfterChoice) return;
    this.mobileQuery = window.matchMedia("(max-width: 749px)");
    this.onChoice = () => {
      window.kcStickyChoiceMade = true;
      this.handleScroll();
    };
    this.onQueryChange = () => this.handleScroll();
    document.addEventListener("variant:change", this.onChoice);
    document.addEventListener("kc-fabric-sheet:close", this.onChoice);
    this.mobileQuery.addEventListener("change", this.onQueryChange);
    this.handleScroll();
  }

  teardownRevealAfterChoice() {
    if (!this.revealAfterChoice) return;
    document.removeEventListener("variant:change", this.onChoice);
    document.removeEventListener("kc-fabric-sheet:close", this.onChoice);
    this.mobileQuery.removeEventListener("change", this.onQueryChange);
  }

  setupInitialState() {
    this.style.opacity = "0";
    this.style.transition = `opacity ${this.fadeInDuration}ms ease-in-out`;
    this.style.visibility = "hidden";
  }

  setupScrollListener() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  }

  removeScrollListener() {
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
  }

  handleScroll() {
    if (this.revealAfterChoice && this.mobileQuery && this.mobileQuery.matches) {
      let show = Boolean(window.kcStickyChoiceMade);
      if (show) {
        const colourStep = document.querySelector(".variant-picker-mobile-accordion[data-kc-open-key]");
        if (colourStep) show = colourStep.getBoundingClientRect().top < window.innerHeight * 0.5;
      }
      if (show && !this.isVisible) this.fadeIn();
      else if (!show && this.isVisible) this.fadeOut();
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const threshold = this.desktopThreshold !== null && this.desktopThreshold !== "" && this.desktopQuery.matches ? Number(this.desktopThreshold) : this.scrollThreshold;

    if (scrollTop > threshold && !this.isVisible) {
      this.fadeIn();
    } else if (scrollTop <= threshold && this.isVisible) {
      this.fadeOut();
    }
  }

  fadeIn() {
    this.style.opacity = "1";
    this.isVisible = true;
    this.style.visibility = "visible";
    // Mirror the .is-active pattern used by other sticky bars in the theme
    // (blocks/sticky-container.liquid). The WhatsApp float section relies on
    // this class to detect visibility and align itself against the PDP bar.
    this.classList.add("is-active");
  }

  fadeOut() {
    this.style.opacity = "0";
    this.isVisible = false;
    this.style.visibility = "hidden";
    this.classList.remove("is-active");
  }

  /* ------------------------------------------------------------------------
     PRICE TOTAL
     Keeps the sticky bar's displayed price in sync with the upsells the
     customer has selected — including the variant chosen for each upsell.
     Base price comes from data-base-price (the main product's selected
     variant, refreshed by Liquid on every section re-render); each selected
     upsell's price comes from data-current-variant-price on its
     [data-upsell-matcher] item (kept current by upsell-variant-selector.js).
     ------------------------------------------------------------------------ */
  setupPriceSync() {
    this.productId = this.getAttribute("data-product-id");
    this.basePrice = parseInt(this.getAttribute("data-base-price"), 10);
    this.baseCompareAtPrice =
      parseInt(this.getAttribute("data-base-compare-at-price"), 10) || 0;
    this.moneyFormat =
      this.getAttribute("data-money-format") ||
      (window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.moneyFormat) ||
      "";
    this._recalcRaf = null;

    if (isNaN(this.basePrice)) return; // nothing to total against

    this.onUpsellChange = (event) => {
      if (
        event.type === "upsell:variant-changed" ||
        (event.target &&
          typeof event.target.matches === "function" &&
          event.target.matches('input[name="upsell"]'))
      ) {
        this.scheduleRecalc();
      }
    };
    this.onVariantRerendered = () => this.scheduleRecalc();

    document.addEventListener("change", this.onUpsellChange);
    document.addEventListener("upsell:variant-changed", this.onUpsellChange);
    document.addEventListener("variant:rerendered", this.onVariantRerendered);

    // Defer the first paint one frame so the upsell block has hydrated and
    // (after a section re-render) the preserved <kc-upsell> has been restored.
    this.scheduleRecalc();
  }

  teardownPriceSync() {
    if (this.onUpsellChange) {
      document.removeEventListener("change", this.onUpsellChange);
      document.removeEventListener(
        "upsell:variant-changed",
        this.onUpsellChange
      );
    }
    if (this.onVariantRerendered) {
      document.removeEventListener(
        "variant:rerendered",
        this.onVariantRerendered
      );
    }
    if (this._recalcRaf) {
      cancelAnimationFrame(this._recalcRaf);
      this._recalcRaf = null;
    }
  }

  scheduleRecalc() {
    if (this._recalcRaf) return;
    this._recalcRaf = requestAnimationFrame(() => {
      this._recalcRaf = null;
      this.recalcTotal();
    });
  }

  /**
   * Upsell items currently selected for this product. Mirrors upsell.js
   * handleFormSubmit: an item counts only if it is visible (not hidden by the
   * product-limit CSS) and checked in every kc-upsell block it appears in
   * (AND across blocks). Deduped by product key. Returns [data-upsell-matcher]
   * containers.
   */
  collectSelectedUpsells() {
    const roots = document.querySelectorAll(
      `kc-upsell[data-product-id="${this.productId}"]`
    );
    const stateByKey = new Map();

    roots.forEach((root) => {
      root.querySelectorAll('input[name="upsell"]').forEach((checkbox) => {
        const item = checkbox.closest("[data-upsell-matcher]");
        if (!item) return;
        if (getComputedStyle(item).display === "none") return;

        const key =
          checkbox.dataset.upsellProductKey || checkbox.dataset.index;
        if (!key) return;

        const existing = stateByKey.get(key);
        if (!existing) {
          stateByKey.set(key, { allChecked: checkbox.checked, item });
        } else {
          existing.allChecked = existing.allChecked && checkbox.checked;
        }
      });
    });

    const selected = [];
    stateByKey.forEach((entry) => {
      if (entry.allChecked) selected.push(entry.item);
    });
    return selected;
  }

  recalcTotal() {
    if (!this.priceListEl || !this.priceListEl.isConnected) {
      this.priceListEl = this.querySelector(".price-list");
    }
    if (!this.priceListEl || isNaN(this.basePrice)) return;

    let total = this.basePrice;
    let totalCompare =
      this.baseCompareAtPrice > this.basePrice
        ? this.baseCompareAtPrice
        : this.basePrice;

    this.collectSelectedUpsells().forEach((item) => {
      let price = parseInt(item.dataset.currentVariantPrice, 10);
      if (isNaN(price)) return; // can't price this item — skip rather than guess
      let compareAt =
        parseInt(item.dataset.currentVariantCompareAtPrice, 10) || 0;
      // the Black Weeks cover costs its set price: the model's discount off its list price, as on the offer card
      // (kc-bf-offer.js coverPricesOf); the voucher keeps its own amount
      if (item.classList.contains("upsell-block-item--bf-cover")) {
        const config = item.closest("kc-upsell")?.querySelector("[data-bf-config]");
        const off = config && !config.hasAttribute("data-kc-voucher-variant-id")
          ? parseInt(config.getAttribute("data-bf-cover-off"), 10) || 0
          : 0;
        if (off > 0) {
          if (compareAt < price) compareAt = price;
          price = Math.round((price * (100 - off)) / 100);
        }
      }
      total += price;
      totalCompare += compareAt > price ? compareAt : price;
    });

    this.renderPrice(total, totalCompare);
  }

  renderPrice(total, totalCompare) {
    const onSale = totalCompare > total;
    const saleClass = onSale
      ? "text-on-sale price-regular-weight"
      : "text-subdued price-regular-weight";

    let html = `<sale-price class="${saleClass}">${this.formatMoney(
      total
    )}</sale-price>`;

    if (onSale) {
      html += `<compare-at-price class="text-subdued line-through price-regular-weight">${this.formatMoney(
        totalCompare
      )}</compare-at-price>`;
    }

    this.priceListEl.innerHTML = html;
  }

  /**
   * Format a price in cents using the shop's money format. Mirrors the
   * formatter in bundle-summary.js (DE/US/FR/CH thousand separators) and
   * strips a trailing ",00"/".00" to match price-list.liquid.
   */
  formatMoney(priceInCents) {
    if (typeof priceInCents !== "number" || isNaN(priceInCents)) return "";

    const amount = priceInCents / 100;
    const amountWithDot = amount.toFixed(2);
    const amountWithComma = amountWithDot.replace(".", ",");
    const amountNoDecimals = Math.floor(amount).toString();

    let formatted;
    if (this.moneyFormat) {
      const amountWithCommaThousands = amountWithDot.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );
      const amountWithDotThousands = amountWithComma.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        "."
      );
      const amountWithSpaceThousands = amountWithDot.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        " "
      );
      const amountWithApostrophe = amountWithDot.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        "'"
      );

      formatted = this.moneyFormat
        .replace("{{amount_with_comma_separator}}", amountWithDotThousands)
        .replace(
          "{{amount_no_decimals_with_comma_separator}}",
          amountNoDecimals
        )
        .replace("{{amount_with_space_separator}}", amountWithSpaceThousands)
        .replace("{{amount_with_apostrophe_separator}}", amountWithApostrophe)
        .replace("{{amount_no_decimals}}", amountNoDecimals)
        .replace("{{amount}}", amountWithCommaThousands);
    } else {
      formatted = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    }

    // price-list.liquid strips zero cents from the displayed price; match it,
    // but only on the cents group (not a ".00" inside a thousands-separated
    // integer like "1.001").
    return formatted.replace(/([.,])00(?=\D|$)/, "");
  }
}

if (!customElements.get("product-detail-sticky")) {
  customElements.define("product-detail-sticky", ProductDetailSticky);
}
